/**
 * Utility functions for generating custom nanoid slugs.
 * 
 * This module provides a centralized way to generate alphanumeric-only slugs
 * without hyphens and underscores, ensuring consistency across the application.
 */

import { customAlphabet } from 'nanoid';

// Custom alphabet for slug generation (alphanumeric only, no hyphens or underscores)
const ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

// Create a custom nanoid generator with fixed 10-character length
const generateSlug = customAlphabet(ALPHABET, 10);

/**
 * Generate a custom nanoid slug using only alphanumeric characters.
 * 
 * @param size - The length of the generated slug (default: 10)
 * @returns A string containing only alphanumeric characters (a-z, A-Z, 0-9)
 * 
 * @example
 * ```typescript
 * const slug = generateSlug();
 * console.log(slug); // e.g., "EK9GZnCcGN"
 * ```
 */
export function generateSlugUtil(size: number = 10): string {
  if (size === 10) {
    // Use the pre-configured generator for the default size
    return generateSlug();
  }
  
  // Create a temporary generator for custom sizes
  const customGenerator = customAlphabet(ALPHABET, size);
  return customGenerator();
}

/**
 * Generate a slug specifically for campaigns.
 * 
 * @returns A 10-character alphanumeric slug for campaign URLs
 */
export function generateCampaignSlug(): string {
  return generateSlug();
}

/**
 * Generate a slug specifically for ads.
 * 
 * @returns A 10-character alphanumeric slug for ad URLs
 */
export function generateAdSlug(): string {
  return generateSlug();
}

/**
 * Generate a slug specifically for organizations.
 * 
 * @returns A 10-character alphanumeric slug for organization URLs
 */
export function generateOrganizationSlug(): string {
  return generateSlug();
}

// Export the alphabet constant for any special use cases
export const SLUG_ALPHABET = ALPHABET;
